import { Component, Input, OnInit, inject } from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {Currency, IconColors} from '../../../global';
import {CurrencyTransformPipe, DateTransformPipe} from '../../pipes';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { ChartComponent } from '../chart/chart.component';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { LoaderComponent } from '../loader/loader.component';
import { CurrencyTransformPipe as CurrencyTransformPipe_1 } from '../../pipes/currency-transform.pipe';

@Component({
    selector: 'slm-cash-flow-chart',
    template: `
    <ng-container *ngIf="data as items; else loading">
      <ng-container *ngIf="data?.length; else noData">
        <slm-chart
          *ngIf="chartData$ | async as chart"
          class="pos-relative block w-100"
          [chartMaxHeight]="chartMaxHeight"
          [height]="maxHeight"
          [data]="chart.data"
          [colors]="chartColor"
          [options]="chart.options"
          type="bar"
          [labels]="chart.labels"
          [legend]="false"
        ></slm-chart>
        <div class="w-100 block">
          <div class="w-100 border-collapse border-top border-color-light-gray">
            <div class="border-bottom border-color-light-gray flex">
              <div class="txt-medium  pt-1 pb-1 flex flex-space-btwn w-max-90 w-100">
                {{'REVENUE_INCOME' | translate}}
                <span
                  class="w-10px h-10px block ml-1 mt-1"
                  [style.backgroundColor]="iconColors.BLUE"
                ></span>
              </div>
              <div
                *ngFor="let item of items"
                class="txt-end w-max-calc-100_7 w-100 m-auto ml-0 mr-0"
              >
                <div class="block w-max-140 m-auto">
                  {{ item.incomes | currencyTransform: currency }}
                </div>
              </div>
            </div>
            <div class="border-bottom border-color-light-gray flex">
              <div class="txt-medium  pt-1 pb-1 flex flex-space-btwn w-max-90 w-100">
                {{'REVENUE_OUTGOING' | translate}}
                <span
                  class="w-10px h-10px block ml-1 mt-1"
                  [style.backgroundColor]="iconColors.RED_MAT"
                ></span>
              </div>
              <div
                *ngFor="let item of items"
                class="txt-end w-max-calc-100_7 w-100 m-auto ml-0 mr-0"
              >
                <div class="block w-max-140 m-auto">
                  {{ item.outgoings | currencyTransform: currency }}
                </div>
              </div>
            </div>
            <div class="border-bottom border-color-light-gray flex">
              <div class="txt-medium  pt-1 pb-1 flex flex-space-btwn w-max-90 w-100">{{'BALANCE' | translate}}
              </div>
              <div
                *ngFor="let item of items"
                class="txt-end w-max-calc-100_7 w-100 m-auto ml-0 mr-0"
              >
                <div class="block w-max-140 m-auto txt-medium">
                  {{ item.balance | currencyTransform: currency }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ng-container>
    </ng-container>
    <ng-template #noData>
      <h4 class="mt-6 mb-6" translate="LBL_MESSAGES.NO_DATA_FOUND"></h4>
    </ng-template>
    <ng-template #loading>
      <slm-loader class="block m-auto mt-5 mb-5"></slm-loader>
    </ng-template>
  `,
    providers: [DateTransformPipe, CurrencyTransformPipe],
    imports: [NgIf, ChartComponent, NgFor, TranslateDirective, LoaderComponent, AsyncPipe, TranslatePipe, CurrencyTransformPipe_1]
})
export class CashFlowChartComponent implements OnInit {
  private readonly datePipe = inject(DateTransformPipe);
  private readonly currencyPipe = inject(CurrencyTransformPipe);

  public readonly chartColor: Array<any> = [
    {
      backgroundColor: IconColors.BLUE
    },
    {
      backgroundColor: IconColors.PINK
    },
    {
      backgroundColor: 'rgba(0, 0, 0, 0.0)',
      borderColor: IconColors.BLUE,
      borderWidth: 2,
      pointBackgroundColor: IconColors.BLUE,
      pointRadius: 5,
      //    borderDashOffset: 5,
      borderDash: [2, 3]
    }
  ];

  public readonly chartOptions = {
    scaleShowVerticalLines: false,
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        ticks: {
          font: {
            weight: 'bold',
            size: 14
          }
        }
      },
      y: {
        ticks: {
          padding: 25,
          callback: (value, _, __) => {
            if (Math.abs(value) > 1_000_000) {
              return `${(value / 1_000_000).toFixed(1)} M`;
            } else if (Math.abs(value) > 1_000) {
              return `${(value / 1_000).toFixed(0)} K`;
            } else {
              return value;
            }
          }
        }
      }
    }
  };

  public readonly iconColors = IconColors;
  public chartData$: Observable<any>;
  public data: any = null;
  public currency: Currency = null;
  private refresh$ = new BehaviorSubject<boolean>(false);
  @Input() labels: Array<string> = [];
  @Input() maxHeight: number = null;
  @Input() chartMaxHeight: number = null;
  @Input() A8Integrating = false;

  @Input('data')
  public set setData(data: any) {
    this.data = data;
    if (data) {
      this.refresh$.next(true);
    }
  }

  @Input('currency')
  public set setCurrency(currency: any) {
    this.currency = currency;
    if (this.currency) {
      this.refresh$.next(true);
    }
  }

  ngOnInit() {
    this.chartData$ = this.refresh$.pipe(
      filter((value) => !!value && !!this.data && !!this.currency),
      map(() => ({
        labels: this.data.map((item) =>
          this.datePipe.transform(new Date(item.date), 'FORMAT.DATE')
        ),
        options: this.getChartOptions(this.currency),
        data: [
          ...this.A8Integrating?
            [{
              data: this.data.map((item) => item.balance),
              type: 'line',
              backgroundColor: 'rgba(0, 0, 0, 0.0)',
              pointHoverBackgroundColor: IconColors.DARK_BLUE,
              borderColor: IconColors.DARK_BLUE,
              pointHoverBorderWidth: 3,
              pointHoverBorderColor: IconColors.DARK_BLUE,
              borderWidth: 2,
              tension: 0.3,
              pointBackgroundColor: IconColors.BLUE,
              pointRadius: 5,
              pointStyle: 'circle',
              borderDash: [2, 3]
            }]: [],
          {
            data: this.data.map((item) => item.incomes),
            barPercentage: 0.6,
            backgroundColor: IconColors.BLUE,
            hoverBackgroundColor: IconColors.DARK_BLUE
          },
          {
            data: this.data.map((item) =>
              item.outgoings === 0 ? 0 : -item.outgoings
            ),
            backgroundColor: IconColors.PINK,
            hoverBackgroundColor: IconColors.RED_MAT,
            barPercentage: 0.6
          }
        ]
      }))
    );
  }

  private getChartOptions = (currency: Currency) => ({
    ...this.chartOptions,
    plugins: {
      tooltip: {
        displayColors: false,
        titleAlign: 'center',
        bodyAlign: 'center',
        callbacks: {
          title: (tooltipItem) => this.currencyPipe.transform(tooltipItem[0].raw, currency)
        },
        label: (tooltipItem) => this.labels[tooltipItem.datasetIndex]
      }
    }
  });
}
